import { useState, useEffect, useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import {
    Trash2,
    RefreshCw,
    Search,
    Plus,
    Filter,
    Eye,
    LayoutTemplate,
    CheckCircle2,
    Clock3,
    XCircle,
    MessageSquareText,
    ExternalLink,
    X
} from 'lucide-react';
import { Card, Stack, Skeleton, Tooltip } from '@mui/material';
import { useReduxLeads } from '@Hooks/reduxLeadsHook/useReduxLeads';
import WhatsappTemplatePreview from '@CommonRepresentationalDisplay/WhatsappTemplatePreview';
import WhatsAppTemplateBuilder from '@FormForms/WhatsAppTemplateBuilder';
import DeleteTemplateForm from '@FormForms/DeleteTemplateForm';
import KaiziqLeadsConnectionService from '@Helpers/URLHelpers/WeboutService/KaiziqLeadsConnectionService';
import ErrorHandler from '@Root/ErrorHandler';
import './WhatsAppTemplateView.scss';

export default function WhatsAppTemplateView() {

    const className = 'whatsapp-template';
    const { currentLeadsId, currentLeads, setTemplateList } = useReduxLeads();
    const [loading, setLoading] = useState(true);
    const [templates, setTemplates] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [statusFilter, setStatusFilter] = useState('ALL');
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [showTemplateForm, setShowTemplateForm] = useState(false);
    const [showTemplateDeleteForm, setShowTemplateDeleteForm] = useState(false);
    const [showTemplatePreview, setShowTemplatePreview] = useState(false);
    const kaiziqLeadsConnectionService = new KaiziqLeadsConnectionService();
    const errorHandler = new ErrorHandler();

    useEffect(() => {
        if (currentLeadsId) {
            renderData();
        }
    }, [currentLeadsId]);

    const renderData = async () => {
        if (!currentLeadsId) {
            setTemplates([]);
            setLoading(false);
            return;
        }
        setLoading(true);
        try {
            let response = await kaiziqLeadsConnectionService.getTemplateListByLeadsIdWithLimit(currentLeadsId, 50);
            if (response.status < 400) {
                response = JSON.parse(response.body);
                const list = response.data || [];
                setTemplateList(list);
                setTemplates(list);
            }
        } catch (error) {
            errorHandler.handleError(error);
        } finally {
            setLoading(false);
        }
    };

    const filteredTemplates = useMemo(() => {
        const query = searchText.trim().toLowerCase();
        return templates.filter((template) => {
            const matchesSearch =
                !query ||
                template.name
                    ?.toLowerCase()
                    .includes(query);

            const matchesStatus =
                statusFilter === 'ALL' ||
                template.status === statusFilter;

            return matchesSearch && matchesStatus;
        });
    }, [
        templates,
        searchText,
        statusFilter
    ]);

    const stats = useMemo(() => {
        return {
            total: templates.length,

            approved: templates.filter(
                (template) =>
                    template.status === 'APPROVED'
            ).length,

            pending: templates.filter(
                (template) =>
                    template.status === 'PENDING'
            ).length,

            rejected: templates.filter(
                (template) =>
                    template.status === 'REJECTED'
            ).length
        };
    }, [templates]);

    const getStatusClass = (status) => {
        switch (status) {
            case 'APPROVED':
                return 'approved';

            case 'PENDING':
                return 'pending';

            case 'REJECTED':
                return 'rejected';

            default:
                return 'unknown';
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'APPROVED':
                return <CheckCircle2 size={14} />;

            case 'PENDING':
                return <Clock3 size={14} />;

            case 'REJECTED':
                return <XCircle size={14} />;

            default:
                return <Clock3 size={14} />;
        }
    };

    const openDeleteModal = (template) => {
        setSelectedTemplate(template);
        setShowTemplateDeleteForm(true);
    };

    const closeDeleteModal = () => {
        setShowTemplateDeleteForm(false);
        setSelectedTemplate(null);
    };

    const openTemplatePreview = (template) => {
        setSelectedTemplate(template);
        setShowTemplatePreview(true);
    };

    const closeTemplatePreview = () => {
        setShowTemplatePreview(false);
        setSelectedTemplate(null);
    };

    const getManageTemplateUrl = (template) => {
        return (
            'https://business.facebook.com/latest/whatsapp_manager/message_templates/?business_id=' +
            currentLeads?.businessId +
            '&tab=message-templates&childRoute=MM_LITE&id=' +
            template.id +
            '&nav_ref=whatsapp_manager&asset_id=' +
            currentLeads?.wabaId
        );
    };

    const MessageTemplateSkeleton = () => (
        <Card className="template-skeleton">
            <Stack spacing={2}>
                <div className="skeleton-heading">
                    <Skeleton
                        variant="circular"
                        width={38}
                        height={38}
                    />

                    <div>
                        <Skeleton
                            variant="text"
                            width={150}
                            height={25}
                        />

                        <Skeleton
                            variant="text"
                            width={90}
                            height={18}
                        />
                    </div>
                </div>

                <Skeleton
                    variant="rounded"
                    height={180}
                />

                <Skeleton
                    variant="text"
                    width="80%"
                />

                <Skeleton
                    variant="rounded"
                    width="100%"
                    height={38}
                />
            </Stack>
        </Card>
    );

    if (showTemplateForm) {
        return (
            <WhatsAppTemplateBuilder
                renderData={renderData}
                onClose={() =>
                    setShowTemplateForm(false)
                }
            />
        );
    }

    return (
        <>
            <div className={className}>

                <div className={`${className}__header`}>
                    <div className='flex-row-center-0p5rem-gap'>
                        <LayoutTemplate size={18} />
                        <h1>WhatsApp Templates </h1>
                    </div>
                    <div className="template-header-actions">
                        <div className="template-search">
                            <Search size={18}/>
                            <input
                                type="text"
                                placeholder="Search templates..."
                                value={searchText}
                                onChange={(e) =>
                                    setSearchText(e.target.value)
                                }
                            />
                        </div>
                        <button
                            className="primary-btn"
                            onClick={() => setShowTemplateForm(true)}
                        >
                            <Plus size={16}/>
                            Create Template
                        </button>
                        <button
                            className="secondary-btn"
                            onClick={renderData}
                        >
                            <RefreshCw size={16}/>
                            Refresh
                        </button>
                    </div>
                </div>


                <main
                    className={`${className}__body`}
                >

                    <div className="content-heading">
                        <div>
                            <h2>
                                Your templates
                            </h2>

                            <p>
                                {loading
                                    ? 'Loading templates...'
                                    : `${filteredTemplates.length} template${
                                          filteredTemplates.length !==
                                          1
                                              ? 's'
                                              : ''
                                      } found`}
                            </p>
                        </div>
                    </div>


                    {loading && (
                        <div className="template-grid">
                            {Array.from({
                                length: 6
                            }).map(
                                (_, index) => (
                                    <MessageTemplateSkeleton
                                        key={
                                            index
                                        }
                                    />
                                )
                            )}
                        </div>
                    )}
                    {!loading &&
                        filteredTemplates.length ===
                            0 && (
                            <div className="template-empty">
                                <div className="empty-icon">
                                    <Eye size={32} />
                                </div>

                                <h3>
                                    {searchText ||
                                    statusFilter !==
                                        'ALL'
                                        ? 'No matching templates'
                                        : 'No templates yet'}
                                </h3>

                                <p>
                                    {searchText ||
                                    statusFilter !==
                                        'ALL'
                                        ? 'Try changing your search or filter.'
                                        : 'Create your first WhatsApp template to get started.'}
                                </p>

                                {searchText ||
                                statusFilter !==
                                    'ALL' ? (
                                    <button
                                        type="button"
                                        className="secondary-btn"
                                        onClick={() => {
                                            setSearchText(
                                                ''
                                            );
                                            setStatusFilter(
                                                'ALL'
                                            );
                                        }}
                                    >
                                        Clear filters
                                    </button>
                                ) : (
                                    <button
                                        type="button"
                                        className="primary-btn"
                                        onClick={() =>
                                            setShowTemplateForm(
                                                true
                                            )
                                        }
                                    >
                                        <Plus size={16} />

                                        Create Template
                                    </button>
                                )}
                            </div>
                        )}

                    {!loading &&
                        filteredTemplates.length >
                            0 && (
                            <div className="template-grid">
                                {filteredTemplates.map(
                                    (template) => {
                                        const manageTemplateUrl =
                                            getManageTemplateUrl(
                                                template
                                            );

                                        return (
                                            <article
                                                key={
                                                    template.id
                                                }
                                                className={`${className}__card`}
                                            >
                                                <div className="template-card-header">
                                                    <div className="template-name">
                                                        <div className="template-name-icon">
                                                            <MessageSquareText
                                                                size={
                                                                    17
                                                                }
                                                            />
                                                        </div>

                                                        <div>
                                                            <h3
                                                                title={
                                                                    template.name
                                                                }
                                                            >
                                                                {
                                                                    template.name
                                                                }
                                                            </h3>

                                                           <span className='template-name__category'>
                                                               {template?.category}
                                                           </span>
                                                        </div>
                                                    </div>

                                                    <span
                                                        className={`template-status ${getStatusClass(
                                                            template.status
                                                        )}`}
                                                    >
                                                        {getStatusIcon(
                                                            template.status
                                                        )}

                                                        {
                                                            template.status
                                                        }
                                                    </span>
                                                </div>
                                                <div className="template-preview">
                                                    <WhatsappTemplatePreview
                                                        id={
                                                            template.id
                                                        }
                                                        hideBackground
                                                    />
                                                </div>
                                                <div className="template-card-footer">
                                                    <button
                                                        type="button"
                                                        className="view-template-btn"
                                                        onClick={() =>
                                                            openTemplatePreview(
                                                                template
                                                            )
                                                        }
                                                    >
                                                        <Eye
                                                            size={
                                                                15
                                                            }
                                                        />

                                                        View
                                                        full
                                                    </button>

                                                    <NavLink
                                                        to={
                                                            manageTemplateUrl
                                                        }
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="manage-btn"
                                                        title="Manage template"
                                                    >
                                                        <ExternalLink
                                                            size={
                                                                15
                                                            }
                                                        />
                                                    </NavLink>

                                                    <Tooltip title="Delete template">
                                                        <button
                                                            type="button"
                                                            className="delete-btn"
                                                            onClick={() =>
                                                                openDeleteModal(
                                                                    template
                                                                )
                                                            }
                                                            aria-label="Delete template"
                                                        >
                                                            <Trash2
                                                                size={
                                                                    16
                                                                }
                                                            />
                                                        </button>
                                                    </Tooltip>
                                                </div>
                                            </article>
                                        );
                                    }
                                )}
                            </div>
                        )}
                </main>
            </div>

            {showTemplatePreview && selectedTemplate && (
                <div
                    className="template-preview-modal-overlay"
                    onMouseDown={(event) => {
                        if (event.target === event.currentTarget) {
                            closeTemplatePreview();
                        }
                    }}
                >
                    <div className="template-preview-modal">
                        <div className="template-preview-modal__header">
                            <div className="template-preview-modal__title">
                                <div className="template-preview-modal__icon">
                                    <MessageSquareText size={18} />
                                </div>

                                <div>
                                    <h2>
                                        {selectedTemplate.name}
                                    </h2>

                                   <span className='template-name__category'>
                                       {selectedTemplate?.category}
                                   </span>
                                </div>
                            </div>

                            <div className="template-preview-modal__header-actions">
                                <span
                                    className={`template-status ${getStatusClass(
                                        selectedTemplate.status
                                    )}`}
                                >
                                    {getStatusIcon(
                                        selectedTemplate.status
                                    )}

                                    {selectedTemplate.status}
                                </span>

                                <button
                                    type="button"
                                    className="modal-close-btn"
                                    onClick={closeTemplatePreview}
                                >
                                    <X size={18} />
                                </button>
                            </div>
                        </div>

                        <div className="template-preview-modal__body">
                            <div className="preview-label">
                                <Eye size={15} />
                                Template preview
                            </div>

                            <div className="full-preview-container">
                                <WhatsappTemplatePreview
                                    id={selectedTemplate.id}
                                    hideBackground
                                    showReadMore={false}
                                />
                            </div>
                        </div>

                        <div className="template-preview-modal__footer">
                            <div className="template-info">
                                <span>Template ID</span>

                                <strong>
                                    {selectedTemplate.id}
                                </strong>
                            </div>

                            <div className="modal-footer-actions">
                                <button
                                    type="button"
                                    className="secondary-btn"
                                    onClick={closeTemplatePreview}
                                >
                                    Close
                                </button>

                                <NavLink
                                    to={getManageTemplateUrl(
                                        selectedTemplate
                                    )}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="primary-btn"
                                >
                                    Manage template
                                    <ExternalLink size={15} />
                                </NavLink>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {showTemplateDeleteForm && (
                <DeleteTemplateForm
                    open={
                        showTemplateDeleteForm
                    }
                    closePopup={
                        closeDeleteModal
                    }
                    template={
                        selectedTemplate
                    }
                    reloadData={
                        renderData
                    }
                />
            )}
        </>
    );
}